import React, { useState, useEffect } from 'react';
import useScrollLock from '../../../hooks/useScrollLock';
import { createPortal } from 'react-dom';
import { FaYoutube } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';

interface CustomMusicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (youtubeUrl: string) => void;
  currentUrl?: string;
}

const CustomMusicModal: React.FC<CustomMusicModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentUrl = '',
}) => {
  const [youtubeUrl, setYoutubeUrl] = useState(currentUrl);
  const [error, setError] = useState('');

  // Prevent background scrolling
  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setYoutubeUrl(currentUrl);
      setError('');
    }
  }, [isOpen, currentUrl]);

  const validateYouTubeUrl = (url: string): boolean => {
    if (!url.trim()) return false;

    // YouTube URL patterns
    const patterns = [
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/,
      /^https?:\/\/youtube\.com\/watch\?v=[\w-]+/,
      /^https?:\/\/youtu\.be\/[\w-]+/,
      /^https?:\/\/www\.youtube\.com\/embed\/[\w-]+/,
    ];

    return patterns.some(pattern => pattern.test(url));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!youtubeUrl.trim()) {
      setError('Please add a YouTube link to continue');
      return;
    }

    if (!validateYouTubeUrl(youtubeUrl)) {
      setError('We support youtube.com and youtu.be links');
      return;
    }

    onSave(youtubeUrl.trim());
    onClose();
  };

  const handleClose = () => {
    setYoutubeUrl(currentUrl);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-[#FFF8F0] rounded-2xl p-8 max-w-md w-full border border-gray-200/60 shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="space-y-1">
            <h2 className="text-2xl font-medium text-gray-900">
              Use Your Own Focus Music
            </h2>
            <p className="text-sm text-gray-600 font-light">
              Add a YouTube link for background music. Instrumental or ambient tracks work best.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
          >
            <IoClose />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="youtube-url" className="block text-sm font-normal text-gray-700 mb-2">
              YouTube music link
            </label>
            <input
              id="youtube-url"
              type="text"
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.target.value);
                setError('');
              }}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-white border border-gray-200/60 rounded-xl sm:rounded-2xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-sm sm:text-base font-normal"
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 font-normal">{error}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 bg-white border border-gray-200/60 hover:border-gray-300/60 text-gray-700 rounded-2xl font-normal transition-all duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl font-medium transition-all duration-300"
            >
              Use this music
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CustomMusicModal;
